import './navbar.css'

import { useModal } from '@ebay/nice-modal-react'
import { Link, useNavigate } from '@tanstack/router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import avatar_3 from '../../assets/common/avatar_3.webp'
import coin_anim from '../../assets/common/coin_anim.gif'
import { IS_COUNTRY } from '../../constants/countries'
import useGeoCheck from '../../hooks/useGeoCheck'
import useRefreshBalance from '../../hooks/useRefreshBalance'
import { useBoundStore } from '../../store/store'
import classNames from '../../utils/classNames'
import Icon from '../icon'
import WalletManagementModal from '../wallet-management/wallet-management-modal'
import Balance from './balance'
import BalanceBreakdown from './balance-breakdown'
import LanguageSelector from './language-selector'
import LoginModal from './log-in-modal'
import LogoutModal from './log-out-modal'

interface NavbarProps {
  hideBalance?: boolean
}

const Navbar = ({ hideBalance }: NavbarProps) => {
  const isCoinAnimVisible = useBoundStore(
    state => state.common.isCoinAnimVisible,
  )
  const isFullScreen = useBoundStore(state => state.common.isFullScreen)
  const profile_picture = useBoundStore(state => state.user.profile_picture)
  const username = useBoundStore(state => state.user.username)
  const navigate = useNavigate()
  const { geoCheck } = useGeoCheck()
  const logInModal = useModal(LoginModal)
  const logOutModal = useModal(LogoutModal)
  const walletManagementModal = useModal(WalletManagementModal)
  const {
    t,
    i18n: { language },
  } = useTranslation(['common', 'drawer', 'walletManagement', 'blogs'])
  const { refreshBalance, isLoading: balanceLoading } = useRefreshBalance()
  const [profileError, setProfileError] = useState(false)

  useEffect(() => {
    if (!username) return
    refreshBalance()
  }, [username])

  return (
    <div
      className="flx-btw-ctr w-full bg-primary shadow-[0px_1px_5px_rgba(0,0,0,0.1)] transition-all duration-[0.2s] ease-[ease-in-out] fixed z-[100] h-[60px] px-10 py-0 top-0 xl:px-[50px] xl:py-0 3xl:px-[150px] 3xl:py-2.5 4xl:px-[250px] 4xl:py-2.5"
      style={{ top: isFullScreen ? -60 : 0 }}>
      <div
        className="flx-ctr"
        style={{ flex: 1, justifyContent: 'flex-start', marginRight: 80 }}>
        <Link to="/" className="flx-ctr min-w-[78px]">
          <img
            className="w-[100px] aspect-[471_/_104] mr-[30px]"
            src="https://sfgdefe0923.fun88adrods.online/mexicoimages/logo/dark.webp"
            alt="Company Logo"
          />
        </Link>
      </div>
      <div className="flx-btw-ctr w-[700px] mr-[100px]">
        {IS_COUNTRY.MX ? (
          language === 'zh' ? (
            <>
              <Link
                className="font-semibold text-base text-highlight relative uppercase link"
                to="/sports/im">
                {t('drawer:sports')} 1
              </Link>
              <Link
                className="font-semibold text-base text-highlight relative uppercase link"
                to="/sports/bti">
                {t('drawer:sports')} 2
              </Link>
              <Link
                className="font-semibold text-base text-highlight relative uppercase link"
                to="/sports">
                {t('drawer:sports')} 3
              </Link>
            </>
          ) : (
            <>
              <Link
                className="font-semibold text-base text-highlight relative uppercase link"
                to="/sports/bti">
                {t('drawer:sports')} 1
              </Link>
              <Link
                className="font-semibold text-base text-highlight relative uppercase link"
                to="/sports">
                {t('drawer:sports')} 2
              </Link>
            </>
          )
        ) : (
          <Link
            className={`font-semibold text-[16px] text-highlight relative uppercase link`}
            to="/sports/bti">
            {t('drawer:sports')}
          </Link>
        )}
        <Link
          className={`font-semibold text-[16px] text-highlight relative uppercase link`}
          to="/casino/$category"
          params={{ category: 'popular' }}>
          CASINO
        </Link>
        <Link
          className={`font-semibold text-[16px] text-highlight relative uppercase link`}
          to="/promotions">
          {t('drawer:promotions')}
        </Link>
        <Link
          className="font-semibold text-base text-highlight relative uppercase link"
          to="/blogs">
          {t('blogs:blogs')}
        </Link>
      </div>
      <div className="flx-ctr flex-1 justify-end">
        {username ? (
          <>
            <div className="flx-ctr">
              <div
                className="relative cursor-pointer mr-1.5"
                onClick={() =>
                  walletManagementModal.show({
                    walletManagementDefaultTab: { tab: 0 },
                  })
                }>
                <Icon className="fill-txt" id="wallet" width="22" height="22" />
                {isCoinAnimVisible && (
                  <img
                    className="absolute left-[-165px] w-[150px] animate-fadeIn pointer-events-none rotate-[30deg] -top-8"
                    src={coin_anim}
                    alt="Coins"
                  />
                )}
              </div>
              {!hideBalance &&
                (IS_COUNTRY.MX ? <Balance /> : <BalanceBreakdown />)}
            </div>
            <button
              onClick={refreshBalance}
              className={classNames(
                'flex mr-[9px] p-1.5',
                balanceLoading && 'animate-rotate-1.5',
              )}>
              <Icon
                id="refresh-balance"
                width="17"
                height="17"
                fill="#D2D4D6"
              />
            </button>
            {IS_COUNTRY.MX && (
              <button
                className="font-Syncopate-Bold bg-highlight text-center text-xs text-white transition-all duration-[0.2s] ease-[ease-in-out] block uppercase px-[17px] py-2.5 rounded-sm"
                style={{ marginRight: '15px' }}
                onClick={() => {
                  walletManagementModal.show({
                    walletManagementDefaultTab: { tab: 0 },
                  })
                }}>
                {t('deposit', { ns: 'drawer' })}
              </button>
            )}
            <Link to="/profile">
              <div className="navbar-icon w-8 h-8 bg-transparent overflow-hidden bg-white mr-[15px] rounded-[50%] aspect-square">
                <img
                  className="w-[30px] h-[30px] object-cover"
                  src={
                    profile_picture && !profileError
                      ? profile_picture
                      : avatar_3
                  }
                  alt="Avatar"
                  onError={() => setProfileError(true)}
                />
              </div>
            </Link>
            <button
              type="submit"
              aria-label="logout"
              className="flex fill-txt cursor-pointer"
              onClick={() => logOutModal.show()}>
              <Icon id="logout" width="22" height="20" />
            </button>
          </>
        ) : (
          <>
            <span
              className="flx-ctr h-[35px] bg-highlight mr-2.5 px-3 py-0 rounded-md cursor-pointer"
              onClick={() => geoCheck(logInModal.show)}>
              <p className="text-xs font-light text-primary-btn-txt uppercase">
                {t('login')}
              </p>
            </span>
            <span
              className="flx-ctr h-[35px] bg-primary-btn-bg px-3 py-0 rounded-md cursor-pointer"
              onClick={() =>
                geoCheck(() =>
                  navigate({
                    to: '/register',
                  }),
                )
              }>
              <p className="text-xs font-light text-primary-btn-txt uppercase">
                {t('register')}
              </p>
            </span>

            {IS_COUNTRY.MX && <LanguageSelector />}
          </>
        )}
      </div>
    </div>
  )
}

export default Navbar
